import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { projectsMessages } from '@/i18n/messages/projects'
import { Select } from '@/components/Select'
import { projectStatuses } from './status'

interface Header {
  name: string | null
  code: string | null
  codeInternal: string | null
  workingName: string | null
  shortName: string | null
  contactName: string | null
  contractNumber: string | null
  description: string | null
  status: number | null
  planingStart: string | null
  planingEnd: string | null
  tbStart: string | null
  tbEnd: string | null
  planedMetres: number | null
  planedHp: number | null
  contractValue: number | null
  projectValue: number | null
  providerId: string | null
  romId: string | null
  projectManagerId: string | null
}

interface Lookups {
  providers: { id: string; name: string | null }[]
  employees: { id: string; name: string | null }[]
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

const INPUT =
  'w-full rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950'

/**
 * Edit a project via a modal — explicitly, not by clicking a cell. Pre-fills from the full
 * project detail, shows the fields grouped like AU's own form, and saves the fields you changed
 * as corrections in aufmass.project_override (layered over AU on read). AU is never written.
 *
 * The dropdown/foreign-key fields (Provider, ROM, Project manager, price lists, calc type) are a
 * deliberate follow-up: they need lookup lists and are handled in a second pass.
 */
export function EditProjectDialog({ projectId, name, onClose }:
  { projectId: string; name: string | null; onClose: () => void }) {
  const t = useT(projectsMessages)
  const tc = useT(commonMessages)
  const { data, isPending } = useQuery<{ header: Header }>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<{ header: Header }>(`/api/projects/${projectId}`),
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" onClick={onClose}
    >
      <div
        className="flex max-h-[min(88vh,100dvh-2rem)] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-neutral-900 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b border-neutral-200 p-5 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold">{t('editProject')}</h2>
            <p className="mt-0.5 text-sm text-neutral-500">{name ?? t('unnamed')}</p>
          </div>
          <button onClick={onClose} aria-label={tc('close')}
                  className="grid size-10 shrink-0 place-items-center rounded-xl text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200">✕</button>
        </div>

        {isPending || !data
          ? <div className="p-10 text-center text-sm text-neutral-500">{tc('loading')}</div>
          : <EditForm projectId={projectId} header={data.header} onClose={onClose} />}
      </div>
    </div>
  )
}

function EditForm({ projectId, header, onClose }:
  { projectId: string; header: Header; onClose: () => void }) {
  const t = useT(projectsMessages)
  const tc = useT(commonMessages)
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const s = (v: string | number | null | undefined) => (v == null ? '' : String(v))
  const d = (v: string | null) => (v ? v.slice(0, 10) : '')
  const g = (v: string | null) => (!v || v === EMPTY_GUID ? '' : v)

  const { data: lookups } = useQuery<Lookups>({
    queryKey: ['project-lookups'],
    queryFn: () => api.get<Lookups>('/api/projects/lookups'),
    staleTime: 5 * 60_000,
  })

  // Field name (the override key) → current value.
  const [f, setF] = useState<Record<string, string>>({
    provider_id: g(header.providerId),
    rom: g(header.romId),
    project_manager: g(header.projectManagerId),
    working_name: s(header.workingName),
    project_name: s(header.name),
    project_short_name: s(header.shortName),
    project_code_internal: s(header.codeInternal),
    project_code_external: s(header.code),
    contact_name: s(header.contactName),
    contract_number: s(header.contractNumber),
    project_description: s(header.description),
    status: s(header.status),
    planing_start: d(header.planingStart),
    planing_end: d(header.planingEnd),
    tb_start: d(header.tbStart),
    tb_end: d(header.tbEnd),
    planed_metres: s(header.planedMetres),
    hp_planed: s(header.planedHp),
    contract_value: s(header.contractValue),
    project_value: s(header.projectValue),
  })
  const set = (k: string) => (v: string) => setF((prev) => ({ ...prev, [k]: v }))

  const original: Record<string, string> = {
    provider_id: g(header.providerId), rom: g(header.romId), project_manager: g(header.projectManagerId),
    working_name: s(header.workingName), project_name: s(header.name),
    project_short_name: s(header.shortName), project_code_internal: s(header.codeInternal),
    project_code_external: s(header.code), contact_name: s(header.contactName),
    contract_number: s(header.contractNumber), project_description: s(header.description),
    status: s(header.status), planing_start: d(header.planingStart), planing_end: d(header.planingEnd),
    tb_start: d(header.tbStart), tb_end: d(header.tbEnd), planed_metres: s(header.planedMetres),
    hp_planed: s(header.planedHp), contract_value: s(header.contractValue), project_value: s(header.projectValue),
  }

  const save = useMutation({
    mutationFn: async () => {
      const changed = Object.keys(f).filter((k) => f[k] !== original[k])
      await Promise.all(changed.map((k) =>
        api.put(`/api/projects/${projectId}/overrides/${k}`, { value: f[k] === '' ? null : f[k] })))
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] })
      void qc.invalidateQueries({ queryKey: ['project', projectId] })
      onClose()
    },
    onError: (e) =>
      setError(e instanceof ApiError ? `${e.message}${e.detail ? ` — ${e.detail}` : ''}` : t('couldNotSave')),
  })

  return (
    <>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
        <Section title={t('identity')}>
          <Field label={t('workingName')}><input className={INPUT} value={f.working_name} onChange={(e) => set('working_name')(e.target.value)} /></Field>
          <Field label={t('projectName')}><input className={INPUT} value={f.project_name} onChange={(e) => set('project_name')(e.target.value)} /></Field>
          <Field label={t('projectShortName')}><input className={INPUT} value={f.project_short_name} onChange={(e) => set('project_short_name')(e.target.value)} /></Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('codeInternal')}><input className={INPUT} value={f.project_code_internal} onChange={(e) => set('project_code_internal')(e.target.value)} /></Field>
            <Field label={t('codeExternal')}><input className={INPUT} value={f.project_code_external} onChange={(e) => set('project_code_external')(e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('contactName')}><input className={INPUT} value={f.contact_name} onChange={(e) => set('contact_name')(e.target.value)} /></Field>
            <Field label={t('contractNumber')}><input className={INPUT} value={f.contract_number} onChange={(e) => set('contract_number')(e.target.value)} /></Field>
          </div>
          <Field label={t('status')}>
            <Select value={f.status ?? ''} onChange={set('status')} empty={{ value: '', label: t('noneOption') }}
              options={projectStatuses().map((st) => ({ value: String(st.code), label: `${st.code} · ${st.label}` }))} />
          </Field>
          <Field label={t('description')}>
            <textarea rows={4} className={INPUT} value={f.project_description} onChange={(e) => set('project_description')(e.target.value)} />
          </Field>
        </Section>

        <Section title={t('assignment')}>
          <Field label={t('provider')}>
            <Select value={f.provider_id ?? ''} onChange={set('provider_id')} empty={{ value: '', label: t('noneOption') }}
              options={(lookups?.providers ?? []).map((o) => ({ value: o.id, label: o.name ?? '' }))} />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('rolloutManager')}>
              <Select value={f.rom ?? ''} onChange={set('rom')} empty={{ value: '', label: t('noneOption') }}
                options={(lookups?.employees ?? []).map((o) => ({ value: o.id, label: o.name ?? '' }))} />
            </Field>
            <Field label={t('projectManager')}>
              <Select value={f.project_manager ?? ''} onChange={set('project_manager')} empty={{ value: '', label: t('noneOption') }}
                options={(lookups?.employees ?? []).map((o) => ({ value: o.id, label: o.name ?? '' }))} />
            </Field>
          </div>
        </Section>

        <Section title={t('planningValues')}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('planningStart')}><input type="date" className={INPUT} value={f.planing_start} onChange={(e) => set('planing_start')(e.target.value)} /></Field>
            <Field label={t('planningEnd')}><input type="date" className={INPUT} value={f.planing_end} onChange={(e) => set('planing_end')(e.target.value)} /></Field>
            <Field label={t('tbStart')}><input type="date" className={INPUT} value={f.tb_start} onChange={(e) => set('tb_start')(e.target.value)} /></Field>
            <Field label={t('tbEnd')}><input type="date" className={INPUT} value={f.tb_end} onChange={(e) => set('tb_end')(e.target.value)} /></Field>
            <Field label={t('tbPlannedM')}><input type="number" className={INPUT} value={f.planed_metres} onChange={(e) => set('planed_metres')(e.target.value)} /></Field>
            <Field label={t('hpPlannedPcs')}><input type="number" className={INPUT} value={f.hp_planed} onChange={(e) => set('hp_planed')(e.target.value)} /></Field>
            <Field label={t('contractValueEur')}><input type="number" className={INPUT} value={f.contract_value} onChange={(e) => set('contract_value')(e.target.value)} /></Field>
            <Field label={t('projectValueEur')}><input type="number" className={INPUT} value={f.project_value} onChange={(e) => set('project_value')(e.target.value)} /></Field>
          </div>
        </Section>

        <p className="text-xs text-neutral-400">
          {t('editNote')}
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-neutral-200 p-5 dark:border-neutral-800">
        {error
          ? <p role="alert" className="text-sm" style={{ color: 'var(--viz-critical)' }}>{error}</p>
          : <span />}
        <div className="flex gap-2">
          <button onClick={onClose}
                  className="rounded-lg px-3 py-1.5 text-sm text-neutral-600 ring-1 ring-neutral-300 hover:bg-neutral-100 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800">{tc('cancel')}</button>
          <button onClick={() => { setError(null); save.mutate() }} disabled={save.isPending}
                  className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60">
            {save.isPending ? t('saving') : tc('save')}
          </button>
        </div>
      </div>
    </>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}
